import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { setReactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import { ClassicEditor } from 'ckeditor5'
import { TextElement } from 'components/WorkspaceEditor/services/TextElementParser'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import { getBaseElementId } from 'components/WorkspaceEditor/utils'
import { getContentItemViewNodeByElementId } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import { MutableRefObject, useCallback, useLayoutEffect, useRef } from 'react'
import { v4 as newGuid } from 'uuid'
import { useWorkspace2Store } from '../../../Workspace2Store'
import {
    buildNodesWithInsertedUOM,
    buildTextElBase,
} from '../utils/uomCursorHelpers'

interface InsertUOMRefs {
    textElement: TextContentElement
    setUnsavedData: (data: string) => void
    updateTreeViewNodes: (nodes: Partial<ContentItemViewNodesType>[]) => void
    debouncedOnChange: { cancel: () => void }
    deactivateCKEditor: () => void
    processEditorContent: (html: string) => TextElement[]
}

interface UseInsertUOMAtCursorParams extends InsertUOMRefs {
    editorRef: MutableRefObject<ClassicEditor | undefined>
    skipNextBlurRef: MutableRefObject<boolean>
}

/**
 * Encapsulates the "insert UOM at cursor" action for the OM workspace plain-text editor.
 * Reads cursor position from CKEditor, delegates the tree transform to buildNodesWithInsertedUOM,
 * then applies the result and focuses the new UOM node.
 */
export const useInsertUOMAtCursor = ({
    editorRef,
    skipNextBlurRef,
    textElement,
    setUnsavedData,
    updateTreeViewNodes,
    debouncedOnChange,
    deactivateCKEditor,
    processEditorContent,
}: UseInsertUOMAtCursorParams): (() => void) => {
    const uomInsertionRefs = useRef<InsertUOMRefs>({
        textElement,
        setUnsavedData,
        updateTreeViewNodes,
        debouncedOnChange,
        deactivateCKEditor,
        processEditorContent,
    })

    useLayoutEffect(() => {
        uomInsertionRefs.current = {
            textElement,
            setUnsavedData,
            updateTreeViewNodes,
            debouncedOnChange,
            deactivateCKEditor,
            processEditorContent,
        }
    }, [
        textElement,
        setUnsavedData,
        updateTreeViewNodes,
        debouncedOnChange,
        deactivateCKEditor,
        processEditorContent,
    ])

    return useCallback(() => {
        const editor = editorRef.current
        if (!editor) return

        const refs = uomInsertionRefs.current
        const textEl = refs.textElement
        if (!textEl.id) return

        refs.debouncedOnChange.cancel()
        skipNextBlurRef.current = true
        refs.deactivateCKEditor()

        try {
            const selection = editor.model.document.selection
            // offset is relative to the caret's parent block — correct only for single-block content,
            // which is the expected case here (Enter is intercepted before a second block can form).
            const offset = selection.getFirstPosition()?.offset ?? 0
            const elementId = getBaseElementId(textEl.id) ?? textEl.id
            const {
                contentItemViewNodes: currentNodes,
                actions,
            } = useWorkspace2Store.getState()
            const paragraphNode = getContentItemViewNodeByElementId(
                currentNodes,
                elementId
            )
            const annotated = refs.processEditorContent(editor.getData())
            const newUOMId = newGuid()
            const authoredUserId = userInfo()?.currentUserInfo?.kpUserId ?? ''

            const textElBase = buildTextElBase(
                textEl as TextContentElement & {
                    authoredUserId?: string
                }
            )

            const buildResult = buildNodesWithInsertedUOM({
                currentNodes,
                paragraphNode,
                elementId,
                textElBase: textElBase as Record<string, unknown>,
                offset,
                annotated,
                newUOMId,
                authoredUserId,
            })

            if (!buildResult) return

            refs.updateTreeViewNodes(buildResult.nodes)
            refs.setUnsavedData('')
            setReactiveWorkspaceActions({
                isInsertUOM: false,
                omWorkspaceDirectInsertUOMHandler: undefined,
            })
            actions.setTextContentElementToAdd({ id: newUOMId })
        } catch (err) {
            setReactiveWorkspaceActions({
                isInsertUOM: false,
                omWorkspaceDirectInsertUOMHandler: undefined,
            })
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useInsertUOMAtCursor] insert failed:', err)
            }
        }
    }, [editorRef, skipNextBlurRef])
}
